const VALIDATION_STATUSES = new Set([400, 409, 422]);

const SERVICE_UNAVAILABLE_STATUSES = new Set([429, 500, 502, 503, 504]);

export const PAYMENT_ERROR_MESSAGES = {
	paymentMethodIssue:
		"We could not process this payment method. Please review your payment details and try again. If the problem continues, contact a TQL representative for assistance.",
	serviceUnavailable:
		"We were unable to process your payment right now. Please try again in a few minutes. If the problem continues, contact a TQL representative for assistance.",
	fallback:
		"We were unable to process your payment. Please try again. If the problem continues, contact a TQL representative for assistance.",
} as const;

/**
 * A sanitized, structured error thrown by the payment API layer.
 *
 * Preserves the upstream HTTP `status` (and raw `details`) so it can be
 * mapped to user-safe copy exactly once, at the user-facing boundary
 * (`usePrepayTenderFlow`), instead of being lost the moment a generic
 * `Error` is thrown.
 */
export class PaymentApiError extends Error {
	readonly status?: number;
	readonly details?: unknown;

	constructor(message: string, status?: number, details?: unknown) {
		super(message);
		this.name = "PaymentApiError";
		this.status = status;
		this.details = details;
	}
}

type ErrorWithStatus = {
	status?: unknown;
	response?: {
		status?: unknown;
	};
};

function getHttpStatus(error: unknown): number | null {
	if (!error || typeof error !== "object") {
		return null;
	}

	const typedError = error as ErrorWithStatus;
	if (typeof typedError.status === "number") {
		return typedError.status;
	}

	if (typedError.response && typeof typedError.response === "object") {
		const { status } = typedError.response;
		if (typeof status === "number") {
			return status;
		}
	}

	return null;
}

function isNetworkOrTransportError(error: unknown): boolean {
	if (!(error instanceof Error)) {
		return false;
	}

	const normalizedMessage = error.message.toLowerCase();
	if (
		normalizedMessage.includes("failed to fetch") ||
		normalizedMessage.includes("network") ||
		normalizedMessage.includes("fetch failed") ||
		normalizedMessage.includes("load failed") ||
		normalizedMessage.includes("timeout")
	) {
		return true;
	}

	return error instanceof TypeError;
}

function isPaymentMethodIssue(error: unknown): boolean {
	const httpStatus = getHttpStatus(error);
	if (httpStatus !== null && VALIDATION_STATUSES.has(httpStatus)) {
		return true;
	}

	let message = "";
	if (typeof error === "string") {
		message = error;
	} else if (error instanceof Error) {
		message = error.message;
	}

	return /declin|insufficient|invalid|expired|verification|bank account|payment method|nonce|token/i.test(
		message,
	);
}

export function mapPaymentErrorToUserMessage(error: unknown): string {
	const httpStatus = getHttpStatus(error);

	if (httpStatus !== null && SERVICE_UNAVAILABLE_STATUSES.has(httpStatus)) {
		return PAYMENT_ERROR_MESSAGES.serviceUnavailable;
	}

	if (httpStatus !== null && VALIDATION_STATUSES.has(httpStatus)) {
		return PAYMENT_ERROR_MESSAGES.paymentMethodIssue;
	}

	if (isNetworkOrTransportError(error)) {
		return PAYMENT_ERROR_MESSAGES.serviceUnavailable;
	}

	if (isPaymentMethodIssue(error)) {
		return PAYMENT_ERROR_MESSAGES.paymentMethodIssue;
	}

	return PAYMENT_ERROR_MESSAGES.fallback;
}
